import React, { useContext, useEffect, useState } from "react";
import { Grid, Paper, TextField, Typography, useTheme } from "@mui/material";
import getData from "../../api/services/DeliveryGuy/getDeliveryGuy";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/InfoContext";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import updateCalculator from "../../api/calculator/updateCalculator";
import getRequiredUserData from "../../utils/getBranchInfo";
import useDocumentById from "../../hooks/useDocumentById";
import generateCustomID from "../../utils/generateCustomID";
import useTableData from "../../hooks/useTableData";

const initialValues = {
  200: 0,
  100: 0,
  50: 0,
  10: 0,
  5: 0,
  1: 0,
};

const updateFields = (targetObject, sourceObject) => {
  for (const key in sourceObject) {
    if (key in targetObject) {
      targetObject[key] = sourceObject[key];
    }
  }
};

const Calculator = () => {
  const theme = useTheme();
  const [data, setData] = useState({});
  const { user } = useAuth();
  const { openSnackbar } = useSnackbar();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const [userClaims, setUserClaims] = useState({});
  const userData = getRequiredUserData();
  useEffect(() => {
    async function fetchUserClaims() {
      try {
        const idTokenResult = await user.getIdTokenResult();
        setUserClaims(idTokenResult.claims);
      } catch (error) {}
    }
    fetchUserClaims();
  }, [user]);

  let active = userData.active;

  const { documentData: documentData2 } = useDocumentById(
    "Budget",
    userData.requiredId ? userData.requiredId : "try"
  );

  const { documentData } = useDocumentById("Bank", userData.requiredId);
  console.log("documentData", documentData);
  const [calculator, setCalculator] = useState({
    sum: 0,
    actual: 0,
    balance: 0,
  });

  useEffect(() => {
    if (!active) {
      return;
    }
    const unsubscribe = getData("Calculator", "active", active, setCalculator);
    if (!calculator) {
      setCalculator({
        sum: 0,
        actual: 0,
        balance: 0,
      });
    }
    return () => unsubscribe();
  }, []);

  updateFields(initialValues, calculator);
  useEffect(() => {
    setData(initialValues);
  }, []);

  const handleInputChange = (key, value) => {
    setData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
  };

  const handleKeyPress = async (key) => {
    setIsSubmitting(true);
    if (active === "") {
      openSnackbar(`You do not have a sheet. Create sheet before.`, "info");
      handleInputChange(key, 0);
      setIsSubmitting(false);
      return;
    }
    if (data[key] === "") {
      openSnackbar(`${key} value can not be null.`, "info");
      handleInputChange(key, 0);
      setIsSubmitting(false);
      return;
    }
    try {
      const value = {
        [key]: parseInt(data[key]),
      };
      if (!active) {
        throw {
          response: {
            data: {
              message:
                "Calculator information is not found. Please check your connection, refresh your browser, and try again.",
              type: "error",
            },
          },
        };
      }
      const res = await updateCalculator(user, active, value);
      openSnackbar(`${res.data.message}`, "success");
    } catch (error) {
      if (error.response && error.response.data) {
        openSnackbar(
          error.response.data.message,
          error.response.data.type ? error.response.data.type : "error"
        );
      } else {
        openSnackbar(
          "An unexpected error occurred.Please kindly check your connection.",
          "error"
        );
      }
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!active) {
      openSnackbar(
        `You do not have calculator , Since you do not have active sheet, please create one first!`,
        "info"
      );
    }
  }, [active]);

  const customerCreditTotalId = generateCustomID(
    `${userData.requiredId}-customer`
  );
  const staffCreditTotalId = generateCustomID(`${userData.requiredId}-staff`);
  const dailyCreditTotalId = generateCustomID(`${userData.requiredId}-daily`);

  const { documentData: totalCustomerCredit } = useDocumentById(
    "totalCredit",
    customerCreditTotalId
  );

  const { documentData: totalStaffCredit } = useDocumentById(
    "totalCredit",
    staffCreditTotalId
  );

  const { documentData: totalDailyCredit } = useDocumentById(
    "totalCredit",
    dailyCreditTotalId
  );

  const { data: table } = useTableData(active);

  console.log(
    totalCustomerCredit,
    totalStaffCredit,
    totalDailyCredit,
    table[table.length - 1]?.total
  );

  return (
    <>
      {active && (
        <Paper
          elevation={5}
          style={{
            padding: "20px 50px",
            margin: "20px",
            marginTop: "45px",
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[300],
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h4"
            align="left"
            style={{ fontWeight: "bold", marginBottom: "20px" }}
          >
            Calculator
          </Typography>
          <Grid container spacing={2}>
            {Object.keys(data).map((key) => (
              <Grid item xs={6} key={key}>
                <Typography variant="subtitle1" align="left">
                  {key} Birr
                </Typography>
                <TextField
                  value={data[key]}
                  type="number"
                  onChange={(e) => handleInputChange(key, e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleKeyPress(key);
                    }
                  }}
                  fullWidth
                  disabled={userClaims.superAdmin}
                  sx={{
                    "& input": {
                      textAlign: "center",
                    },
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <Grid
            container
            spacing={2}
            style={{ marginTop: "20px", marginLeft: "30%" }}
          >
            <Grid item xs={4}>
              <Typography variant="subtitle1" align="left">
                Sum
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle1" align="left">
                Actual
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle1" align="left">
                Balance
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle1" align="left">
                {calculator ? calculator?.sum?.toFixed(2) : 0}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle1" align="left">
                {documentData2 && calculator
                  ? (
                      documentData2.budget +
                      (table[table.length - 1]?.total -
                        ((totalCustomerCredit?.customerCreditTotal || 0) +
                          (totalDailyCredit.dailyCreditTotal || 0) +
                          (totalStaffCredit.staffCreditTotal || 0))) -
                      (documentData.total || 0)
                    )?.toFixed(2)
                  : 0}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle1" align="left">
                {documentData2 && calculator
                  ? (
                      calculator.sum.toFixed(2) -
                      (
                        documentData2.budget +
                        (table[table.length - 1]?.total -
                          ((totalCustomerCredit?.customerCreditTotal || 0) +
                            (totalDailyCredit.dailyCreditTotal || 0) +
                            (totalStaffCredit.staffCreditTotal || 0))) -
                        (documentData.total || 0)
                      )?.toFixed(2)
                    )?.toFixed(2)
                  : 0}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
    </>
  );
};

export default Calculator;
